// Story 8.1 (ADO-2010), Tasks 5.1/5.3/5.5: the ordering entry flow, between the
// ordering-choices read and the session store.
//
//   - `applyOrderingChoices` puts a read into the store and, when nothing is chosen yet,
//     makes the sign-in choice (one entry with one account, or a still-valid remembered
//     hint; A1.1, A1.4, A3);
//   - `selectOrderingChoice` is every explicit choice (choice screen, switcher, open
//     cart): it sets the active entry and rewrites the remembered hint (A1.3);
//   - `selectStaffAccount` is a staff user's pick of a store account at an entry (Enabler
//     E8-G, ADO-3109, DA-10, AC11); a staff pick is never remembered on the device;
//   - `reloadOrderingChoices` re-reads (retry, or a refresh of the open-cart counts on the
//     choice screen and, in the background, on a header switch), dropping a reply that lands
//     after a newer reload or a sign-out;
//   - `postSignInDestination` is where `/` sends a signed-in user (A10.1, Task 5.5);
//   - `requiresOrderingChoice` is the browsing-route gate's test (AC4, AC5): a store user
//     with something to choose and no active choice (or no account where the active entry
//     offers several; Story 8.4, ADO-2013) must choose first, and so must a staff user with no
//     store account picked (Enabler E8-G, ADO-3109, DA-10).

import {
  fetchOrderingChoices,
  type OrderingChoices,
  type OrderingStaffAccount,
} from '../api/ordering-choices.client';
import { useSessionStore, type SessionContext } from '../auth/useSessionStore';
import {
  CHOOSE_ROUTE,
  PRODUCTS_ROUTE,
  destinationFor,
  resolveInitialChoice,
} from './orderingChoices';
import { readRememberedHint, writeRememberedHint } from './rememberedHint';

/** The choices, or `null` when the read failed (never throws). */
export async function fetchOrderingChoicesOrNull(): Promise<OrderingChoices | null> {
  try {
    return await fetchOrderingChoices();
  } catch {
    return null;
  }
}

/**
 * Choose `entryKey` (+ account) and, for a store user, remember it on this device for
 * this user. Returns false (and remembers nothing) when the choice is not offered.
 */
export function selectOrderingChoice(
  entryKey: string,
  customerAccountCode: string | null,
  nowMs: number = Date.now(),
): boolean {
  if (!useSessionStore.getState().chooseEntry(entryKey, customerAccountCode)) return false;
  const { tenantId, identity, orderingChoices, activeEntryKey, activeTradingAccount } =
    useSessionStore.getState();
  if (orderingChoices?.scope === 'TRADING_ACCOUNTS' && tenantId !== null && identity !== null) {
    writeRememberedHint(
      tenantId,
      identity.sub,
      {
        entryKey: activeEntryKey ?? entryKey,
        customerAccountCode: activeTradingAccount?.customerAccountCode ?? null,
      },
      nowMs,
    );
  }
  return true;
}

/** Enabler E8-G (ADO-3109), DA-10 (AC11): a staff user orders for the store `account` it picked
 *  at `entryKey`. Returns false when the entry is not offered or the caller is not staff. */
export function selectStaffAccount(entryKey: string, account: OrderingStaffAccount): boolean {
  return useSessionStore.getState().chooseStaffAccount(entryKey, account);
}

/** Apply a read (`null` = failed) and make the sign-in choice when none is active. */
export function applyOrderingChoices(choices: OrderingChoices | null, nowMs: number): void {
  const store = useSessionStore.getState();
  if (choices === null) {
    store.setOrderingFailed();
    return;
  }
  store.setOrderingChoices(choices);
  const { activeEntryKey, tenantId, identity } = useSessionStore.getState();
  if (activeEntryKey !== null) return;
  const hint =
    tenantId !== null && identity !== null
      ? readRememberedHint(tenantId, identity.sub, nowMs)
      : null;
  const initial = resolveInitialChoice(choices, hint);
  if (initial !== null) selectOrderingChoice(initial.entryKey, initial.customerAccountCode, nowMs);
}

let reloadGeneration = 0;

/** Re-read the choices. A reply superseded by a newer reload, or by a sign-out or a new
 *  sign-in while in flight (the identity object is replaced on both), is dropped.
 *  Resolves `true` only when THIS reload's reply was applied, so a caller that acts on the
 *  result (the choice screen's retry) never acts on a superseded one.
 *
 *  `background` (the header switcher's refresh of the open-cart counts) shows no loading
 *  state, and a failed read keeps the last good choices and status, so neither the choice
 *  screen's error notice nor the post-sign-in route reacts to a read nobody asked to see.
 *  The one exception is a shown read this one superseded: its loading state then ends here,
 *  as a failure. */
export async function reloadOrderingChoices({
  background = false,
}: { background?: boolean } = {}): Promise<boolean> {
  const generation = ++reloadGeneration;
  const identity = useSessionStore.getState().identity;
  if (!background) useSessionStore.getState().setOrderingLoading();
  const choices = await fetchOrderingChoicesOrNull();
  if (generation !== reloadGeneration) return false;
  if (identity === null || useSessionStore.getState().identity !== identity) return false;
  if (background && choices === null) {
    if (useSessionStore.getState().orderingStatus === 'loading') {
      useSessionStore.getState().setOrderingFailed();
    }
    return false;
  }
  applyOrderingChoices(choices, Date.now());
  return true;
}

/**
 * Where `/` sends a signed-in user once the bootstrap is ready (a deep link wins over
 * this; see AppRoute). A failed read goes to the choice screen, which offers a retry.
 * A store user goes to the choice screen until a choice is made, then to the
 * destination of the chosen account. Enabler E8-G (ADO-3109), DA-10 (AC11): a staff user has no
 * cart until it picks a store account, so it goes to the choice screen (where it picks one) until
 * it has, then to the product list. A session with no read keeps the product list.
 */
export function postSignInDestination(state: SessionContext): string {
  if (state.orderingStatus === 'error') return CHOOSE_ROUTE;
  const choices = state.orderingChoices;
  if (choices === null) return PRODUCTS_ROUTE;
  if (choices.scope !== 'TRADING_ACCOUNTS') {
    return state.activeTradingAccount === null && choices.entries.length > 0
      ? CHOOSE_ROUTE
      : PRODUCTS_ROUTE;
  }
  if (state.activeEntryKey === null) return CHOOSE_ROUTE;
  return destinationFor(state.activeTradingAccount);
}

/**
 * True when the user must make an ordering choice before browsing (AC4, AC5): a successful
 * choices read is in hand (`orderingChoices` is kept while a refresh is in flight or after a
 * refresh failed, so the gate does not flicker open during one), it has at least one entry,
 * and nothing usable is chosen:
 *   - a store user (`TRADING_ACCOUNTS`) with no active choice; Story 8.4 (ADO-2013), AC1: or an
 *     active entry offering accounts with none of them active (a Value Centre with several,
 *     reached by its depot alone), since the Shopper must select the account to order;
 *   - Enabler E8-G (ADO-3109), DA-10 (AC11): a staff user (`DEPOT_IDS`) with no store account
 *     picked, a depot alone included. A cart is always a store account's, and an SL1.0 rep picks
 *     a store from their list before acting for it (`engine/get_smanager.php:6111-6114`,
 *     `engine/get_users.php:256-274`), so a staff user never browses to order without one.
 * False before any read has succeeded (a failed first read already lands on the choice screen,
 * with its retry), and with no entries (an unlinked Shopper: the choice screen shows the "no
 * accounts" state; browsing is left as is).
 */
export function requiresOrderingChoice(state: SessionContext): boolean {
  const choices = state.orderingChoices;
  if (choices === null || choices.entries.length === 0) return false;
  if (choices.scope === 'DEPOT_IDS') return state.activeTradingAccount === null;
  if (choices.scope !== 'TRADING_ACCOUNTS') return false;
  if (state.activeEntryKey === null) return true;
  const active = choices.entries.find((entry) => entry.key === state.activeEntryKey);
  return active !== undefined && active.accounts.length > 0 && state.activeTradingAccount === null;
}
